import {
    AutomatedCampaignType,
    CampaignBigType,
    CampaignDetail,
    CampaignModel,
    CampaignStatus,
    CampaignSummary,
    CampaignType,
    DeliveryStatus,
} from "./dataForActiveCampaign";
import {FailCallback, sendRequest} from "../../util/http/HTTP";
import {ApiCampaign} from "../../util/http/API";
import {convertUTCtoLocal, formatDateTime, formatDoctorName} from "../../util/util";
import {CampaignStatistic, CampaignStatisticDetail} from "./dataForCampaignStatistic";
import moment from "moment";
import {Language, TimeFormat} from "../../util/enum";
import {TestMessage} from "./sendTestingMessageModal";
import {TemplateContent} from "./templateEditContentList";
import {CampaignListType} from "./campaign";
import {
    AutomatedCampaignFilter,
    BeforeDayForAutomatedCampaign,
    CampaignSetting,
    convertToESTDate,
    FireType,
    getDefaultDataForTemplateLanguage,
} from "./dataForDesignCampaign";
import {DateFilterKey, MessageFilter, MessageStatus} from "./campaignStatisticList";
import {SelectPatient} from "../patients/patientPreviewList";

export interface MessageTemplate {
    language: string,
    libMessage: string,
    compliedMessage: string
}

export interface MessageTemplateContent {
    templateId: string,
    name: string,
    templateContent: Array<TemplateContent>
}

const parseCampaignSummaryFromData = (data: any): CampaignSummary => {
    let doctorName = ""
    if (data.employee) {
        const {employeeFirstName, employeeLastName} = data.employee
        doctorName = formatDoctorName(employeeFirstName, employeeLastName)
    }
    let groupName = ""
    if (data.patientGroups && data.patientGroups.length) {
        const group = data.patientGroups[0]
        groupName = group.groupName
    }

    const sendDateTime = convertUTCtoLocal(data.startSendTime)
    const getCampaignTypeName = (type: number) => {
        const campaignTypeNameList = ["Regular Campaign", "Automated Campaign", "Automated Campaign", ""]
        if (type < campaignTypeNameList.length) {
            return [campaignTypeNameList[type]]
        }
        return ""
    }
    return {
        id: data.campaignId,
        relationId: data.relationId,
        name: data.campaignName,
        sendDateTime: formatDateTime(sendDateTime, data.campaignBigType),
        status: data.campaignStatus,
        type: getCampaignTypeName(data.campaignBigType) + " - " + "SMS",
        campaignBigType: data.campaignBigType,
        automatedCampaignType: data.automatedCampaignType,
        campaignType: data.campaignType,
        patientCount: data.campaignDetailsCount,
        sendFailedCount: data.campaignDetailsFailCount,
        sendSuccessCount: data.campaignDetailsSuccessCount,
        toBeSentCount: data.campaignDetailsSendingCount,
        groupName: groupName,
        createdBy: doctorName,
    }
}

const parseCampaignDetailFromData = (data: any): CampaignDetail => {
    const {patientGroups} = data
    const group = patientGroups && patientGroups.length ? patientGroups[0] : {}
    // todo: -- need return the olid for the current location
    const setting: CampaignSetting = {} as CampaignSetting
    setting.campaignId = data.campaignId
    setting.relationId = data.relationId
    setting.campaignName = data.campaignName
    setting.campaignBigType = data.campaignBigType
    if (data.campaignBigType === CampaignBigType.regularCampaign) {
        setting.fireType = FireType.scheduleLater
    }
    setting.automatedCampaignType = data.automatedCampaignType
    setting.campaignType = data.campaignType

    setting.startDate = convertToESTDate(new Date(data.startSendTime))
    setting.endDate = convertToESTDate(new Date(data.endTime))
    setting.sendDateTime = moment(setting.startDate).format(TimeFormat.HHmm)
    setting.matchingCount = data.campaignDetailsCount
    setting.groupId = data.groupIds?.length ? data.groupIds[0] : ""
    setting.groupName = group.groupName
    setting.messagePerDay = data.maxMessagesPerDay
    setting.beforeDay = data.beforeDay
    setting.templateId = data.sourceMessageTemplateId
    const filter = data.filter || {}
    const planType = (filter.planType instanceof Array && filter.planType.length) ? filter.planType[0] : ""
    setting.filter = {
        fromAge: `${filter.fromAge || ""}`,
        toAge: `${filter.toAge || ""}`,
        planType: planType,
    }
    const defaultTemplateData: Array<TemplateContent> = getDefaultDataForTemplateLanguage()
    let templateContentList = defaultTemplateData
    if (data.messageTemplateDetails) {
        templateContentList = data.messageTemplateDetails.map(({language, libMessage, compliedMessage}: any) => {
            const template = defaultTemplateData.find((item) => item.language === language)
            if (template) {
                return {
                    ...template,
                    language: language,
                    compliedMessage: compliedMessage,
                    libMessage: libMessage,
                }
            }
            return {
                language: language,
                compliedMessage: compliedMessage,
                libMessage: libMessage,
            } as TemplateContent
        })
    }

    return {
        setting: setting,
        templateContentList: templateContentList,
    }
}

const parseCampaignStatisticFromData = (_item: any): CampaignStatistic => {
    const patient = _item.targetPatient
    const dob = patient.dob === "0001-01-01T00:00:00Z" ? "" : moment(convertUTCtoLocal(patient.dob), TimeFormat.YYYYMMDDHHmmss).format(TimeFormat.YYYYMMDD)

    let lastVisit = ""
    if (patient.lastEncounter && patient.lastEncounter.dos) {
        lastVisit = moment(convertUTCtoLocal(patient.lastEncounter.dos), TimeFormat.YYYYMMDDHHmmss).format(TimeFormat.YYYYMMDD)
    }
    let cs = {} as CampaignStatistic
    cs.name = `${patient.firstName} ${patient.lastName}`
    cs.dob = dob
    cs.email = patient.email
    cs.phone = patient.mobilePhone
    cs.gender = patient.gender
    cs.deliveryStatus = _item.campaignDetailStatus
    cs.lastVisit = lastVisit

    let startSendTime: string = _item.startSendTime
    let deliverDate: string = _item.deliverDate
    if (deliverDate.includes("0001-01-01T00:00:00")) {
        cs.deliverTime = cs.deliveryStatus === DeliveryStatus.success ? formatDateTime(startSendTime) : ""
    } else {
        cs.deliverTime = formatDateTime(deliverDate)
    }

    return cs
}

export const getCampaignList = (mEmployeeID: string | number,
    campaignListType: CampaignListType,
    pageIndex: number, pageSize: number,
    success: (list: Array<CampaignModel>, total: number) => void, fail?: FailCallback) => {
    let param: {
        mEmployeeID: string | number,
        pageId: number,
        onePageCount: number,
        campaignStatus?: Array<CampaignStatus>
    } = {
        mEmployeeID: mEmployeeID,
        pageId: pageIndex,
        onePageCount: pageSize,
        campaignStatus: [],
    }

    if (campaignListType === CampaignListType.active) {
        param.campaignStatus = [CampaignStatus.inProgress, CampaignStatus.paused, CampaignStatus.scheduled]
    } else if (campaignListType === CampaignListType.archived) {
        param.campaignStatus = [CampaignStatus.canceled, CampaignStatus.completed]
    }

    sendRequest(ApiCampaign.queryCampaignWeb, param, (response) => {
        const data = response.data
        let list: Array<CampaignModel> = []
        if (data && data.length) {
            list = data.map((_item: any) => {
                return {
                    summary: parseCampaignSummaryFromData(_item),
                    detail: parseCampaignDetailFromData(_item),
                }
            })
        }
        success(list, response.totalCount)
    }, fail)
}

export const getCampaignSettings = (campaignId: string, success: (setting: CampaignSetting, templateContentList: Array<TemplateContent>) => void, fail: FailCallback) => {
    const param = {
        campaignId: campaignId,
    }
    sendRequest(ApiCampaign.queryCampaignWeb, param, (response) => {
        if (response && response.data && response.data.length) {
            const data = response.data[0]
            const {setting, templateContentList} = parseCampaignDetailFromData(data)
            success && success(setting, templateContentList)
        } else {
            fail({code: 0, msg: "No Record"})
        }
    }, fail)
}

const parseDeliverStatusListWith = (messageFilter: MessageFilter): Array<DeliveryStatus> => {
    if (messageFilter.msgStatus === MessageStatus.success) {
        return [DeliveryStatus.success]
    } else if (messageFilter.msgStatus === MessageStatus.fail) {
        return [DeliveryStatus.failed, DeliveryStatus.reject]
    } else if (messageFilter.msgStatus === MessageStatus.inProgress) {
        return [DeliveryStatus.created, DeliveryStatus.sending]
    }
    return []
}

const parseDeliverRangeWith = (messageFilter: MessageFilter) => {
    if (messageFilter.dateFilter === DateFilterKey.today) {
        return {
            fromDate: `${moment().format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
            toDate: `${moment().add(1, "day")
                .format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
        }
    } else if (messageFilter.dateFilter === DateFilterKey.last7Days) {
        return {
            fromDate: `${moment().subtract(7, "day")
                .format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
            toDate: `${moment().format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
        }
    } else if (messageFilter.dateFilter === DateFilterKey.last30Days) {
        return {
            fromDate: `${moment().subtract(30, "day")
                .format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
            toDate: `${moment().format(TimeFormat.YYYYMMDD)}T00:00:00.000Z`,
        }
    }
    return undefined
}

export const getCampaignDetail = (campaignId: string, messageFilter: MessageFilter, pageIndex: number, pageSize: number,
    success: (patientList: CampaignStatisticDetail, total: number) => void,
    fail?: FailCallback) => {

    const param = {
        pageId: pageIndex,
        onePageCount: pageSize,
        campaignIds: [campaignId],
        campaignDetailStatus: parseDeliverStatusListWith(messageFilter),
        dateRange: parseDeliverRangeWith(messageFilter),
    }
    sendRequest(ApiCampaign.queryCampaignDetail, param, (response) => {
        if (response?.code === 0) {
            const campaignDetails = response?.data?.campaignDetails || []
            const list = campaignDetails.map((_item: any) => parseCampaignStatisticFromData(_item))
            const campaignDetailStatusCount = response?.data?.campaignDetailStatusCount || {}
            const total = response.totalCount
            const detail: CampaignStatisticDetail = {
                list: list,
                totalCount: campaignDetailStatusCount.campaignDetailsCount,
                successCount: campaignDetailStatusCount.campaignDetailsSuccessCount,
                failCount: campaignDetailStatusCount.campaignDetailsFailCount,
                pendingCount: campaignDetailStatusCount.campaignDetailsSendingCount,
            }
            success && success(detail, total)
        } else {
            fail && fail({code: response?.code, msg: response?.message})
        }
    }, fail)
}

export const getAutoCampaignDetail = (relationId: string, messageFilter: MessageFilter, pageIndex: number, pageSize: number,
    success: (detail: CampaignStatisticDetail, total: number) => void,
    fail?: FailCallback) => {
    const param = {
        pageId: pageIndex,
        onePageCount: pageSize,
        relationId: relationId,
        campaignDetailStatus: parseDeliverStatusListWith(messageFilter),
        dateRange: parseDeliverRangeWith(messageFilter),
    }
    sendRequest(ApiCampaign.queryAutoCampaignDetail, param, (response) => {
        if (response?.code === 0) {
            const campaignDetails = response?.data?.campaignDetails || []
            const list = campaignDetails.map((_item: any) => parseCampaignStatisticFromData(_item))
            const campaignDetailStatusCount = response?.data?.campaignDetailStatusCount || {}
            const total = response.totalCount
            const detail: CampaignStatisticDetail = {
                list: list,
                totalCount: campaignDetailStatusCount.campaignDetailsCount,
                successCount: campaignDetailStatusCount.campaignDetailsSuccessCount,
                failCount: campaignDetailStatusCount.campaignDetailsFailCount,
                pendingCount: campaignDetailStatusCount.campaignDetailsSendingCount,
            }
            success && success(detail, total)
        } else {
            fail && fail({code: response?.code, msg: response?.message})
        }
    }, fail)
}

export const getPatientsCountByGroup = (groupId: string, success: (count: number) => void, fail: FailCallback) => {
    const param = {
        groupId: groupId,
        onlyGetCount: 1,
    }
    sendRequest(ApiCampaign.getPatientsByGroup, param, (response) => {
        success(response.totalCount)
    }, fail)
}

export interface Employee {
    clinicID: number,
    employeeID: number,
    mEmployeeID: number,
    employeeFirstName: string,
    employeeMidName: string,
    employeeLastName: string
}

export interface MessageTemplateDetails {
    language: string,
    libMessage: string,
    compliedMessage: string
}

export enum SendByActionType {
    sms= 1,
    email= 2
}

export const saveCampaign = (
    campaignName: string,
    // UTC
    startSendTime: string,
    // UTC
    endTime: string,
    campaignBigType: number,
    campaignType: CampaignType,
    automatedCampaignType: AutomatedCampaignType,
    // Message max per day
    campaignLimitType: number,
    maxMessagesPerDay: number,
    messageTemplateId: string | undefined,
    groupIds: Array<string>,
    employee: Employee,
    sourceMessageTemplateId: string | undefined,
    messageTemplateDetails: Array<MessageTemplateDetails>,
    excludePatientIds: Array<SelectPatient>,
    beforeDay: BeforeDayForAutomatedCampaign,
    filter: AutomatedCampaignFilter,
    success: () => void,
    fail: FailCallback
) => {
    const param = {
        campaign: {
            campaignName: campaignName,
            startSendTime: startSendTime,
            endTime: endTime,
            campaignBigType: campaignBigType,
            campaignType: campaignBigType === CampaignBigType.regularCampaign ? campaignType : undefined,
            automatedCampaignType: campaignBigType === CampaignBigType.automatedCampaign ? automatedCampaignType : undefined,
            campaignLimitType: campaignLimitType,
            // no limit for automatedCampaign
            maxMessagesPerDay: campaignBigType === CampaignBigType.regularCampaign ? maxMessagesPerDay : 9999,
            messageTemplateId: messageTemplateId,
            sourceMessageTemplateId: sourceMessageTemplateId,
            messageTemplateDetails: messageTemplateDetails,
            groupIds: groupIds,
            employee: employee,
            excludeFields: [],
            actionTypes: [SendByActionType.sms],
            excludePatientIds: excludePatientIds,
            filter: filter,
            beforeDay: beforeDay,
        },
    }
    sendRequest(ApiCampaign.saveCampaign, param, () => {
        success()
    }, fail)
}

export const sendCampaignTestMessage = (testMessage: TestMessage, success: () => void, fail: FailCallback) => {
    const param = {
        "actionType": testMessage.actionType,
        "title": testMessage.title,
        "message": testMessage.message,
        "email": testMessage.email,
        "mobilePhone": testMessage.mobilePhone,
        "patientId": testMessage.patientId,
        "clinicId": testMessage.clinicId,
        "mPatientId": testMessage.mPatientId,
        "isDone": testMessage.isDone,
    }

    sendRequest(ApiCampaign.sendCampaignTestMessage, param, () => {
        success && success()
    }, fail)
}

export const saveAsTemplate = (
    id: string | undefined, name: string,
    templateList: Array<MessageTemplate>,
    success: () => void,
    fail: FailCallback
) => {
    if (!name || !name.length) {
        return;
    }
    const param = {
        messageTemplate: {
            messageTemplateId: id && id.length ? id : undefined,
            name: name,
            messageTemplateDetails: templateList,
        },
    }
    sendRequest(ApiCampaign.saveMessageTemplate, param, () => {
        success && success()
    }, fail)
}

export const getTemplateList = (
    mEmployeeId: number,
    success: (list: Array<MessageTemplateContent>) => void,
    fail?: FailCallback
) => {
    const param = {
        mEmployeeIDs: [mEmployeeId],
    }
    sendRequest(ApiCampaign.getMessageTemplates, param, (response) => {
        let list: Array<MessageTemplateContent> = []
        if (response && response.data && response.data.length) {
            const { data } = response
            list = data.map((templateOfEmployee: any) => {
                const name = templateOfEmployee.name
                const templateId = templateOfEmployee.messageTemplateId
                const templateContentList: Array<TemplateContent> = templateOfEmployee.messageTemplateDetails.map((_item: any, idx: number) => {
                    const language = _item.language === "Chinese" ? Language.simplifiedChinese : _item.language;
                    return {
                        language: language,
                        id: idx,
                        libMessage: _item.libMessage,
                        compliedMessage: _item.compliedMessage,
                        name: language,
                    }
                })
                return {
                    name: name,
                    templateId: templateId,
                    templateContent: templateContentList,
                }
            })
        }
        success && success(list)
    }, fail)
}

export const updateRegularCampaign = (
    campaignId: string,
    campaignName: string,
    maxMessagesPerDay: number,
    daysBeforeBirthday: number,
    templateContentList: Array<MessageTemplateDetails>,
    starSendTime: string,
    success: (isSuccess: boolean) => void,
    fail: FailCallback
) => {
    const param = {
        "campaignId": campaignId,
        "campaignName": campaignName,
        "maxMessagesPerDay": maxMessagesPerDay,
        "daysBeforeBirthday": daysBeforeBirthday,
        "messageTemplateDetails": templateContentList,
        "starSendTime": starSendTime,
    }
    sendRequest(ApiCampaign.updateCampaign, param, (response) => {
        success && success(response && response.code === 0)
    }, fail)
}

export const updateAutomatedCampaign = (
    campaignId: string,
    campaignName: string,
    maxMessagesPerDay: number,
    daysBeforeBirthday: number,
    templateContentList: Array<MessageTemplateDetails>,
    startSendTime: string,
    relationId: string,
    filter: AutomatedCampaignFilter,
    success: (isSuccess: boolean) => void,
    fail: FailCallback
) => {
    const param = {
        campaignId: campaignId,
        campaignName: campaignName,
        // no limit for automatedCampaign
        maxMessagesPerDay: 9999,
        daysBeforeBirthday: daysBeforeBirthday,
        messageTemplateDetails: templateContentList,
        startSendTime: startSendTime,
        relationId: relationId,
        filter: filter,
    }
    sendRequest(ApiCampaign.updateAutoCampaign, param, (response) => {
        success && success(response && response.code === 0)
    }, fail)
}

export const changeAutoCampaignStatus = (
    campaignId: string,
    relationId: string,
    campaignStatus: CampaignStatus,
    success: (isSuccess: boolean) => void,
    fail: FailCallback
) => {
    const param = {
        "campaignId": campaignId,
        "relationId": relationId,
        "campaignStatus": campaignStatus,
    }
    sendRequest(ApiCampaign.changeAutoCampaignStatus, param, (response) => {
        success && success(response && response.code === 0)
    }, fail)
}

export const changeRegularCampaignStatus = (
    campaignId: string,
    campaignStatus: CampaignStatus,
    success: (isSuccess: boolean) => void,
    fail: FailCallback
) => {
    const param = {
        "campaignId": campaignId,
        "campaignStatus": campaignStatus,
    }
    sendRequest(ApiCampaign.changeRegularCampaignStatus, param, (response) => {
        success && success(response && response.code === 0)
    }, fail)
}
